import type { Directive } from "vue";

interface StickyElementEl extends HTMLElement {
  __stickyEl?: HTMLElement;
  __stickyOnScroll?: (evt: Event) => void;
  __stickyScrollParent?: HTMLElement;
}

/**
 * Walks up the DOM tree from `node` to find the nearest scrollable ancestor.
 */
function getScrollParent(node: HTMLElement): HTMLElement | undefined {
  let scrollParent: HTMLElement | undefined;
  let currentNode = node.parentNode as HTMLElement | null;

  while (!scrollParent) {
    if (currentNode) {
      if (currentNode.scrollHeight > currentNode.clientHeight) {
        scrollParent = currentNode;
      } else {
        if (!currentNode.parentNode) {
          break;
        }
        currentNode = currentNode.parentNode as HTMLElement;
      }
    } else {
      currentNode = node.parentNode as HTMLElement;
      if (!currentNode) {
        break;
      }
    }
  }

  return scrollParent;
}

const StickyElement: Directive<HTMLElement> = {
  updated(el) {
    const target = el as StickyElementEl;
    target.__stickyEl = el;
    const scrollParent = getScrollParent(el);

    if (scrollParent && !target.__stickyOnScroll) {
      target.__stickyOnScroll = (evt: Event) => {
        if (target.__stickyEl) {
          const scrollTarget = evt.target as HTMLElement;
          if (scrollTarget.scrollTop > 40) {
            target.__stickyEl.classList.add("stuck");
          } else if (scrollTarget.scrollTop === 0) {
            target.__stickyEl.classList.remove("stuck");
          }
        }
      };
      scrollParent.addEventListener("scroll", target.__stickyOnScroll);
      target.__stickyScrollParent = scrollParent;
    }
  },

  unmounted(el) {
    const target = el as StickyElementEl;
    if (target.__stickyScrollParent && target.__stickyOnScroll) {
      target.__stickyScrollParent.removeEventListener("scroll", target.__stickyOnScroll);
    }
    if (target.__stickyEl) {
      target.__stickyEl.classList.remove("stuck");
    }
    delete target.__stickyEl;
    delete target.__stickyOnScroll;
    delete target.__stickyScrollParent;
  }
};

export default StickyElement;
